"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Resource } from "@/lib/types/resource";
import { downloadFile, getFileIcon, getFileUrl } from "@/lib/file.util";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  Eye, 
  FileText, 
  FileImage, 
  FileVideo, 
  FileAudio, 
  FileSpreadsheet,
  FileType,
  FileCode,
  FileArchive
} from "lucide-react";
import Link from "next/link";
import mime from "mime-types";

type Props = {
  resource: Resource;
  index: number;
};

const resourceColors = ["text-green-600", "text-blue-600", "text-purple-600"];

const getResourceTypeColor = (type: string) => {
  switch (type) {
    case 'IMAGE':
      return 'bg-blue-100 text-blue-700';
    case 'VIDEO':
      return 'bg-purple-100 text-purple-700';
    case 'AUDIO':
      return 'bg-yellow-100 text-yellow-700';
    case 'DOCUMENT':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const ResourceCard = ({ resource, index }: Props) => {
  const bgColor = resourceColors[index % resourceColors.length];
  const color = resourceColors[index % resourceColors.length];
  
  // Get the first file's type to determine the icon
  const fileType = resource.files?.[0]?.type || '';
  console.log('fileType ==> ', fileType);
  
  const FileIcon = getFileIcon(fileType, resource.resourceType);

  return (
    <Card className="border border-gray-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
      <CardHeader className={`${bgColor} rounded-t-lg relative`}>
        <div className="flex justify-center">
          <FileIcon className={`h-16 w-16 ${color}`} />
        </div>
        <Badge 
          className={`absolute -top-3 right-2 ${getResourceTypeColor(resource.resourceType)}`}
        >
          {resource.resourceType}
        </Badge>
      </CardHeader>
      <CardContent className="pt-6 clear-both h-full">
        <CardTitle className="text-center mb-2">{resource.title}</CardTitle>
        <CardDescription className="text-center">
          {resource.description}
        </CardDescription>
      </CardContent>
      <CardFooter>
        {resource.files && resource.files?.length > 0 ? (
          <div className="flex items-center flex-col lg:flex-row gap-2 w-full">
            <Button
              variant="outline"
              className="w-full lg:w-1/2 flex items-center justify-center gap-2"
              onClick={() => {
                resource.files?.forEach((file) => {
                  downloadFile(getFileUrl(file.id), {
                    fileName: file?.name,
                  });
                });
              }}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
            <Button
              variant="outline"
              className="w-full lg:w-1/2 flex items-center justify-center gap-2"
              asChild
            >
              <Link href={`/file/${resource?.id}/preview`}>
                <Eye className="h-4 w-4" />
                Preview
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {resource.resourceUrl ? (
              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                asChild
              >
                <Link href={`/file/${resource?.id}/preview`}>
                  <Eye className="h-4 w-4" />
                  Preview
                </Link>
              </Button>
            ) : (
              <div className="flex items-center justify-center w-full">
                <p className="text-sm text-gray-500">No files available</p>
              </div>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default ResourceCard;
